/** Small color helpers shared by the Worker and the browser. No dependencies. */

export interface Rgb {
	r: number;
	g: number;
	b: number;
}

export interface Hsl {
	h: number;
	s: number;
	l: number;
}

export function hexToRgb(hex: string): Rgb {
	return {
		r: parseInt(hex.slice(1, 3), 16),
		g: parseInt(hex.slice(3, 5), 16),
		b: parseInt(hex.slice(5, 7), 16),
	};
}

export function rgbToHex({ r, g, b }: Rgb): string {
	return "#" + [r, g, b].map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, "0")).join("");
}

export function clamp(value: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, value));
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
	const rn = r / 255;
	const gn = g / 255;
	const bn = b / 255;
	const max = Math.max(rn, gn, bn);
	const min = Math.min(rn, gn, bn);
	const delta = max - min;
	const l = (max + min) / 2;

	let h = 0;
	if (delta !== 0) {
		if (max === rn) h = ((gn - bn) / delta) % 6;
		else if (max === gn) h = (bn - rn) / delta + 2;
		else h = (rn - gn) / delta + 4;
		h *= 60;
		if (h < 0) h += 360;
	}
	const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
	return { h, s, l };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
	const c = (1 - Math.abs(2 * l - 1)) * s;
	const hp = (((h % 360) + 360) % 360) / 60;
	const x = c * (1 - Math.abs((hp % 2) - 1));
	const [r1, g1, b1] =
		hp < 1 ? [c, x, 0] :
		hp < 2 ? [x, c, 0] :
		hp < 3 ? [0, c, x] :
		hp < 4 ? [0, x, c] :
		hp < 5 ? [x, 0, c] : [c, 0, x];
	const m = l - c / 2;
	return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

/** Relative luminance, per WCAG. */
export function luminance({ r, g, b }: Rgb): number {
	const channel = (value: number): number => {
		const v = value / 255;
		return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Black or white, whichever reads better on top of the given color. */
export function readableInk(hex: string): string {
	return luminance(hexToRgb(hex)) > 0.45 ? "#101014" : "#ffffff";
}

/** How close two colors feel, 0..1, weighing hue most heavily. */
export function closeness(hexA: string, hexB: string): number {
	const a = rgbToHsl(hexToRgb(hexA));
	const b = rgbToHsl(hexToRgb(hexB));
	let hueGap = Math.abs(a.h - b.h);
	if (hueGap > 180) hueGap = 360 - hueGap;
	const hueScore = 1 - hueGap / 180;
	const satScore = 1 - Math.abs(a.s - b.s);
	const lightScore = 1 - Math.abs(a.l - b.l);
	const weight = (a.s + b.s) / 2;
	return clamp(hueScore * (0.4 + 0.3 * weight) + satScore * 0.2 + lightScore * (0.4 - 0.3 * weight), 0, 1);
}
